import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Activity,
  RefreshCw,
  Trash2,
  PlayCircle,
  AlertCircle,
  CheckCircle2,
  Loader2,
  Image as ImageIcon,
  ChevronDown,
  ChevronUp,
  Layers,
} from "lucide-react";

/**
 * Admin "Rescue Queue" tile — everything ops used to do from PowerShell
 * (reset-and-normalize / normalize-all / backfill-hls / clear phantom
 * photo failures) now lives behind four buttons with live counters that
 * poll `/admin/videos/hls-status?failed_samples=5` every 5 seconds.
 *
 * Failure inspector: click the FAILED count to expand a drawer of the
 * latest failed docs with their `hls_error` text — no more Mongo shell
 * for diagnosis.
 */
const POLL_INTERVAL_MS = 5000;

const Counter = ({ label, value, color, testid, onClick, active }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={!onClick}
    data-testid={testid}
    className={`flex-1 min-w-[100px] p-3 border transition-all text-left ${
      onClick ? "cursor-pointer hover:bg-white/5" : "cursor-default"
    } ${active ? "bg-white/5" : ""}`}
    style={{ borderColor: `${color}55` }}
  >
    <p
      className="font-mono text-[9px] uppercase tracking-widest"
      style={{ color }}
    >
      {label}
    </p>
    <p className="font-display text-3xl mt-1 tabular-nums" style={{ color }}>
      {value ?? "—"}
    </p>
  </button>
);

const ActionButton = ({ onClick, busy, disabled, icon: Icon, label, tone, testid }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={busy || disabled}
    data-testid={testid}
    className={`px-3 h-10 border font-mono text-[11px] uppercase tracking-widest flex items-center gap-2 transition-all disabled:opacity-40 ${
      tone === "danger"
        ? "border-[#FF3B30]/50 text-[#FF3B30] hover:bg-[#FF3B30]/10"
        : tone === "warn"
          ? "border-[#FFD700]/60 text-[#FFD700] hover:bg-[#FFD700]/10"
          : "border-[#39FF14]/60 text-[#39FF14] hover:bg-[#39FF14]/10"
    }`}
  >
    {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Icon className="h-3.5 w-3.5" />}
    {label}
  </button>
);

export default function RescueQueue() {
  const [stats, setStats] = useState(null);
  const [samples, setSamples] = useState([]);
  const [showSamples, setShowSamples] = useState(false);
  const [audit, setAudit] = useState([]);
  const [showAudit, setShowAudit] = useState(false);
  const [busy, setBusy] = useState(null); // "normalize" | "hls" | "photos" | null

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/videos/hls-status?failed_samples=5");
      setStats({
        ready: r.data.ready ?? 0,
        generating: r.data.generating ?? 0,
        failed: r.data.failed ?? 0,
        pending: r.data.pending ?? 0,
      });
      setSamples(r.data.failed_samples || []);
    } catch (e) {
      // Silent — polling errors would spam toasts.
    }
  }, []);

  const loadAudit = useCallback(async () => {
    try {
      const r = await api.get("/admin/actions?limit=10");
      setAudit(r.data?.rows || []);
    } catch {
      /* swallow */
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, POLL_INTERVAL_MS);
    return () => clearInterval(t);
  }, [load]);

  const runAction = async (key, path, method = "post", body = null, label) => {
    if (busy) return;
    setBusy(key);
    try {
      const opts = { method };
      if (body) opts.data = body;
      const r = await api.request({ url: path, ...opts });
      const data = r.data || {};
      toast.success(
        data.cleared != null
          ? `${label}: cleared ${data.cleared}`
          : data.queued != null
            ? `${label}: queued ${data.queued}`
            : `${label} ✓`,
      );
      await load();
      if (showAudit) await loadAudit();
    } catch (e) {
      toast.error(`${label} failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusy(null);
    }
  };

  return (
    <section
      data-testid="rescue-queue"
      className="border border-white/10 p-4 mb-8 bg-black/60"
    >
      <div className="flex items-center gap-2 mb-3">
        <Activity className="h-4 w-4 text-[#39FF14]" strokeWidth={2} />
        <h2 className="font-mono text-[11px] uppercase tracking-widest text-white/80">
          rescue queue
        </h2>
        <span className="ml-auto flex items-center gap-1.5 text-[9px] font-mono uppercase tracking-widest text-white/40">
          <span className="h-1.5 w-1.5 rounded-full bg-[#39FF14] animate-pulse" />
          live · 5s
        </span>
      </div>

      {/* Counters */}
      <div className="flex flex-wrap gap-2 mb-4">
        <Counter label="ready" value={stats?.ready} color="#39FF14" testid="rescue-count-ready" />
        <Counter
          label="pending"
          value={stats?.pending}
          color="#00FFFF"
          testid="rescue-count-pending"
        />
        <Counter
          label="generating"
          value={stats?.generating}
          color="#FFD700"
          testid="rescue-count-generating"
        />
        <Counter
          label="failed"
          value={stats?.failed}
          color="#FF3B30"
          testid="rescue-count-failed"
          onClick={() => setShowSamples((v) => !v)}
          active={showSamples}
        />
      </div>

      {/* Action buttons */}
      <div className="flex flex-wrap gap-2 mb-3">
        <ActionButton
          testid="rescue-drain-normalize"
          onClick={() => runAction("normalize", "/admin/videos/normalize-all?batch_size=10", "post", null, "Drain normalize")}
          busy={busy === "normalize"}
          icon={PlayCircle}
          label="drain normalize"
        />
        <ActionButton
          testid="rescue-drain-hls"
          onClick={() => runAction("hls", "/admin/videos/backfill-hls?batch_size=10", "post", null, "Drain HLS")}
          busy={busy === "hls"}
          icon={PlayCircle}
          label="drain hls"
        />
        <ActionButton
          testid="rescue-rebuild-hls"
          onClick={() => {
            if (!window.confirm("Re-render HLS for up to 20 videos that already have a ladder? This kicks off ffmpeg jobs for videos that ALREADY play — only worth it after upgrading the HLS ladder (e.g. new bitrate tier). Continue?")) return;
            runAction("rebuild-hls", "/admin/videos/rebuild-hls-all?limit=20", "post", null, "Rebuild HLS");
          }}
          busy={busy === "rebuild-hls"}
          icon={Layers}
          label="rebuild hls (new ladder)"
          tone="warn"
        />
        <ActionButton
          testid="rescue-clear-photos"
          onClick={() => runAction("photos", "/admin/videos/clear-photo-hls-failures", "post", null, "Clear photo phantoms")}
          busy={busy === "photos"}
          icon={ImageIcon}
          label="clear photo phantoms"
          tone="warn"
        />
        <ActionButton
          testid="rescue-refresh"
          onClick={() => { load(); loadAudit(); }}
          icon={RefreshCw}
          label="refresh"
        />
      </div>

      {/* Failure inspector drawer */}
      {showSamples && (
        <div
          data-testid="rescue-failure-drawer"
          className="border-t border-white/10 pt-3 mt-3"
        >
          <p className="font-mono text-[9px] uppercase tracking-widest text-[#FF3B30] mb-2">
            // last {samples.length} failures
          </p>
          {samples.length === 0 ? (
            <p className="text-white/40 text-xs flex items-center gap-1.5">
              <CheckCircle2 className="h-3.5 w-3.5 text-[#39FF14]" />
              no recent failures
            </p>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {samples.map((s) => (
                <div
                  key={s.id}
                  data-testid={`rescue-fail-${s.id}`}
                  className="border border-[#FF3B30]/30 bg-[#FF3B30]/5 p-2"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <AlertCircle className="h-3 w-3 text-[#FF3B30] shrink-0" />
                    <code className="text-[10px] text-white/70 font-mono truncate">
                      {s.id}
                    </code>
                    {s.video_probe?.video_codec && (
                      <span className="ml-auto text-[9px] font-mono text-white/40 uppercase">
                        {s.video_probe.video_codec}/{s.video_probe.audio_codec || "?"}
                      </span>
                    )}
                  </div>
                  <p className="text-[10px] text-white/60 font-mono break-all whitespace-pre-wrap max-h-24 overflow-y-auto">
                    {s.hls_error || "(no error text stored)"}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Audit trail toggle */}
      <button
        type="button"
        onClick={() => {
          if (!showAudit) loadAudit();
          setShowAudit((v) => !v);
        }}
        data-testid="rescue-audit-toggle"
        className="mt-3 flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-white/40 hover:text-white/70"
      >
        {showAudit ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
        recent audit trail
      </button>
      {showAudit && (
        <div
          data-testid="rescue-audit-drawer"
          className="border-t border-white/10 pt-3 mt-2 space-y-1 max-h-48 overflow-y-auto"
        >
          {audit.length === 0 ? (
            <p className="text-white/40 text-xs">no audit rows yet</p>
          ) : (
            audit.map((row, i) => (
              <div key={i} className="text-[10px] font-mono text-white/60 flex gap-2">
                <span className="text-white/30 shrink-0">
                  {row.at?.slice(11, 19)}
                </span>
                <span className="text-[#00FFFF] shrink-0">{row.action}</span>
                <span className="text-white/50">
                  {row.count != null ? `× ${row.count}` : ""}
                  {row.cleared != null ? `cleared ${row.cleared}` : ""}
                </span>
              </div>
            ))
          )}
        </div>
      )}
    </section>
  );
}
